import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

function getNextBoundaryMs(): number {
  try {
    const stored = sessionStorage.getItem('reporter_carousel_start');
    let start = stored ? parseInt(stored, 10) : Date.now();
    if (!stored) {
      sessionStorage.setItem('reporter_carousel_start', String(start));
    }
    const elapsed = Date.now() - start;
    return TWO_HOURS_MS - (elapsed % TWO_HOURS_MS);
  } catch {
    // sessionStorage unavailable (private browsing, cross-origin iframe)
    return TWO_HOURS_MS;
  }
}

interface CountdownRingProps {
  size?: number;
  strokeWidth?: number;
  color?: string;
  trackColor?: string;
}

export function CountdownRing({
  size = 36,
  strokeWidth = 3,
  color = '#d4af37',
  trackColor = 'rgba(212,175,55,0.2)',
}: CountdownRingProps) {
  const [remainingMs, setRemainingMs] = useState(() => getNextBoundaryMs());
  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef<number>(Date.now());
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;
    const tick = () => {
      const now = Date.now();
      if (now - lastTickRef.current >= 1000) {
        lastTickRef.current = now;
        setRemainingMs(getNextBoundaryMs());
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [reducedMotion]);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = remainingMs / TWO_HOURS_MS;
  const dashOffset = circumference * (1 - progress);

  const totalMins = Math.floor(remainingMs / 60000);
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  const label = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;

  return (
    <div
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      title={`Next theme in ${label}`}
      aria-label={`Next theme in ${label}`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)' }}
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      <span
        style={{
          fontFamily: '"JetBrains Mono", "Courier New", monospace',
          fontSize: 11,
          color,
          letterSpacing: '1px',
          userSelect: 'none',
        }}
      >
        {label}
      </span>
    </div>
  );
}
